import { Trans } from '@lingui/react/macro';
import type { GoogleSearchAdDeliverable } from '../lib/api.js';

// The Google SERP-style preview of a search ad, plus the keywords and
// audience that decide when it shows.
//
// EXTRACTED so there is exactly one of these. It lived inside
// GoogleSearchAdTile on the campaign page, and when the campaign_health
// card's drawer needed to show "the ad this finding is about", the
// drawer got a hand-rolled simplification instead — a worse preview
// with no match-type dots, no audience, and no rationale, sitting three
// inches from the real one in the same product. A founder noticed
// immediately, which is the correct outcome for that kind of shortcut.
//
// Same reasoning as reusing CampaignHealthPanel across both surfaces:
// two places showing the same object must not disagree about what it
// looks like.

/** Google renders roughly 2-3 headlines and ~150 chars of description
 *  in a real SERP slot, so the preview shows what will actually be
 *  seen rather than everything that was written. */
const PREVIEW_HEADLINES = 3;
const PREVIEW_DESCRIPTIONS = 2;
const DESCRIPTION_CHARS = 160;
const KEYWORD_CHIPS = 12;

/** Exported because the review route needs it for the meta tile too —
 *  one copy rather than two that can drift on malformed input. */
export function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export function GoogleAdPreview({
  deliverable,
  /** Drop the keyword / audience / rationale blocks and show only the
   *  SERP card. Used where the surrounding surface already carries that
   *  context and repeating it would be noise. */
  compact = false,
}: {
  deliverable: GoogleSearchAdDeliverable;
  compact?: boolean;
}) {
  const previewHeadlines = deliverable.headlines.slice(0, PREVIEW_HEADLINES);
  const previewDescriptions = deliverable.descriptions.slice(0, PREVIEW_DESCRIPTIONS);
  const descriptionText = previewDescriptions.map((d) => d.text).join(' ');

  return (
    <div>
      <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
        {'↘'}{' '}
        <Trans>
          ad preview · {deliverable.headlines.length} headlines, {deliverable.descriptions.length}{' '}
          descriptions
        </Trans>
      </p>
      {/* Mirrors the actual shape of a sponsored result — Ad badge,
          display URL, blue title, meta description — so the founder
          reads "this is what it looks like in search" rather than a
          structured data table. */}
      <div className="mt-2 max-w-[500px] rounded-lg border border-[var(--line)] bg-white px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-sm border border-[var(--ink)]/40 px-1 py-px font-mono text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]/80">
            <Trans>Ad</Trans>
          </span>
          <span aria-hidden="true" className="text-[var(--ink)]/30">
            ·
          </span>
          <span className="truncate text-[12.5px] text-[var(--ink)]/70">
            {hostnameOf(deliverable.final_url)}
          </span>
        </div>
        <p
          className="mt-1.5 truncate text-[18px] font-medium leading-snug text-[#1a0dab] hover:underline"
          style={{ fontFamily: 'arial, sans-serif' }}
        >
          {previewHeadlines.map((h) => h.text).join(' | ')}
        </p>
        <p
          className="mt-1 text-[13.5px] leading-[1.45] text-[#4d5156]"
          style={{ fontFamily: 'arial, sans-serif' }}
        >
          {descriptionText.slice(0, DESCRIPTION_CHARS)}
          {descriptionText.length > DESCRIPTION_CHARS ? '…' : ''}
        </p>
      </div>

      {compact ? null : (
        <>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            {'↘'} <Trans>keywords · {deliverable.keywords.length}</Trans>
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {deliverable.keywords.slice(0, KEYWORD_CHIPS).map((kw) => (
              <li
                key={`${kw.text}-${kw.matchType}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--ink)]/20 bg-white px-2 py-0.5 font-mono text-[10.5px] text-[var(--ink)]/85"
              >
                {/* Match type is the difference between a keyword that
                    fires on one query and one that fires on hundreds,
                    so it gets a visual rather than a tooltip alone. */}
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full ${
                    kw.matchType === 'EXACT'
                      ? 'bg-[var(--plum)]'
                      : kw.matchType === 'PHRASE'
                        ? 'bg-[var(--spell)]'
                        : 'bg-[var(--ink)]/30'
                  }`}
                  title={kw.matchType.toLowerCase()}
                />
                {kw.text}
              </li>
            ))}
            {deliverable.keywords.length > KEYWORD_CHIPS ? (
              <li className="font-mono text-[10.5px] text-[var(--ink)]/55">
                <Trans>+{deliverable.keywords.length - KEYWORD_CHIPS} more</Trans>
              </li>
            ) : null}
          </ul>

          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            {'↘'} <Trans>audience</Trans>
          </p>
          <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/85">
            {deliverable.audience_brief}
          </p>

          <p className="mag-voice mt-3 text-[13px] leading-[1.5] text-[var(--ink)]/65">
            “{deliverable.why}”
          </p>
        </>
      )}
    </div>
  );
}
